import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { SCPagination } from 'rollup-allspark';
import HelpPopover from '../../../components/HelpPopover';
import {
  DEFAULT_PAGE_NO,
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZES,
} from '../../../config';
import { cn, isEmpty } from '../../../utils';
import { readablePrice } from '../../../utils/getCurrency';
import {
  generateProviderSpecificTableData,
  sortProviderSpecificDataRow,
} from '../../../utils/nativeAnalyticsUtils';

const ItemBreakDownTableWithProvider = ({
  tableData,
  currentTab,
  analyticsProviders,
  itemBreakDownDataLoading,
}) => {
  // HOOKS
  const [isNameColumnHeaderIsOpen, setIsNameColumnHeaderIsOpen] =
    useState(false);
  const [isQuantityColumnHeaderIsOpen, setIsQuantityColumnHeaderIsOpen] =
    useState(false);
  const [isRevenueColumnHeaderIsOpen, setIsRevenueColumnHeaderIsOpen] =
    useState(false);
  const [tableFlattenedData, setTableFlattenedData] = useState({});
  const [sortOrder, setSortOrder] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE_NO);
  const [currentSize, setCurrentSize] = useState(DEFAULT_PAGE_SIZE);
  const { currency, currency_symbol } = tableData || {};
  const currencyObj = { code: currency, symbol: currency_symbol };
  useEffect(() => {
    const indexOfLastPerm = currentPage * currentSize;
    const indexOfFirstPerm = indexOfLastPerm - currentSize;
    let paginatedData = [];
    let sortedData = [];

    const flattenedData = generateProviderSpecificTableData(
      tableData && tableData.analytics && tableData.analytics.length
        ? tableData.analytics
        : [],
    );

    if (
      flattenedData &&
      flattenedData.providerDataRow &&
      flattenedData.providerDataRow.length
    ) {
      sortedData = sortProviderSpecificDataRow(
        flattenedData,
        sortBy,
        sortOrder,
      );
      paginatedData = sortedData.providerDataRow.slice(
        indexOfFirstPerm,
        indexOfLastPerm,
      );
    }

    const paginatedFlattenedData = {
      ...flattenedData,
      providerDataRow: paginatedData,
    };

    setTableFlattenedData(paginatedFlattenedData);
  }, [tableData, sortOrder, sortBy, currentPage, currentSize]);

  const handleSorting = newSortBy => {
    const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newSortOrder);
    setSortBy(newSortBy);
    setCurrentPage(DEFAULT_PAGE_NO);
  };
  const getSortClass = field => {
    let sortClass = 'kt-arrow-sort text-base';
    if (sortOrder === 'asc' && sortBy === field) {
      sortClass = 'kt-arrow-up';
    } else if (sortOrder === 'desc' && sortBy === field) {
      sortClass = 'kt-arrow-down';
    }
    return cn(sortClass, 'cursor-pointer');
  };
  const getRevenueKey = () => {
    if (tableData && tableData.currency === 'USD') {
      return 'revenueUsd';
    }
    return 'revenue';
  };
  const getProviderRevenueKey = () => {
    if (tableData && tableData.currency === 'USD') {
      return 'providerRevenuesInUsd';
    }
    return 'providerRevenues';
  };
  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setCurrentSize(size);
  };
  // RENDER
  const pagination = !itemBreakDownDataLoading ? (
    <SCPagination
      className="mt-4"
      total={tableData && tableData.analytics ? tableData.analytics.length : 0}
      page={currentPage}
      size={currentSize}
      sizes={DEFAULT_PAGE_SIZES}
      onPageChange={handlePageChange}
    />
  ) : null;
  const renderNoDataRow = (
    <tr className="text-center">
      <td colSpan="11" className="p-5 text-sm text-grey-dark">
        No data found
      </td>
    </tr>
  );
  // TABLE HEADER
  const tableHeaderBaseClass =
    'border-b border-grey p-3 text-xs font-semibold uppercase text-grey-darker';
  const tableCellBaseClasses = 'px-3 py-3 border-b border-grey';

  const providerSpecificCells = (rowData, key) => {
    if (!rowData || !rowData[key]) {
      return null;
    }
    const cells = rowData[key];
    return cells.map((cell, index) => {
      if (
        key === 'providerQuantitiesTitle' ||
        key === 'providerRevenuesTitle'
      ) {
        const provider = analyticsProviders.find(p => p.id === cell);
        const showTitle = rowData[key].includes(cell);
        return (
          <th
            key={`${key}-${cell}-${index + 1}`}
            className={cn(
              tableCellBaseClasses,
              'sc-tabular-nums text-grey-darker text-right',
            )}
          >
            {showTitle && provider && provider.title ? provider.title : ''}
          </th>
        );
      }
      return (
        <td
          key={`${key}-${cell}-${index + 1}`}
          className={cn(tableCellBaseClasses, 'sc-tabular-nums text-right')}
        >
          {key === 'providerQuantities'
            ? cell
            : readablePrice(cell, currencyObj)}
        </td>
      );
    });
  };

  const tableHeaders = ['', 'Quantity Ordered', 'Revenue'];
  const renderTableHeader = (
    <thead>
      <tr>
        <th className={tableHeaderBaseClass} rowSpan={2}>
          <div className="flex justify-between">
            <div className="flex">
              {currentTab && currentTab.title ? currentTab.title : ''} Name
              <HelpPopover
                isPopoverOpen={isNameColumnHeaderIsOpen}
                position="right"
                isHtmlElement
                content={`Menu  ${currentTab.title}`}
                setIsPopoverOpen={setIsNameColumnHeaderIsOpen}
                iconClassNames="sc-information cursor-pointer ml-1"
              />
            </div>
            <button
              type="button"
              className={getSortClass('title')}
              onClick={() => handleSorting('title')}
            >
              {/* button content */}
            </button>
          </div>
        </th>
        <th
          className={tableHeaderBaseClass}
          colSpan={
            tableFlattenedData && Object.keys(tableFlattenedData).length
              ? tableFlattenedData.uniqueProvidersLength
              : 0
          }
        >
          <div className="flex justify-between">
            <div className="flex">
              {tableHeaders[1]}
              <HelpPopover
                isPopoverOpen={isQuantityColumnHeaderIsOpen}
                position="right"
                isHtmlElement
                content={`${currentTab.title} Quantity is the total number of times the menu ${currentTab.title} has been ordered from`}
                setIsPopoverOpen={setIsQuantityColumnHeaderIsOpen}
                iconClassNames="sc-information cursor-pointer ml-1"
              />
            </div>
            <button
              type="button"
              className={getSortClass('quantity')}
              onClick={() => handleSorting('quantity')}
            >
              {/* button content */}
            </button>
          </div>
        </th>
        <th
          className={tableHeaderBaseClass}
          colSpan={
            tableFlattenedData && Object.keys(tableFlattenedData).length
              ? tableFlattenedData.uniqueProvidersLength
              : 0
          }
        >
          <div className="flex justify-between">
            <div className="flex">
              {tableHeaders[2]}
              <HelpPopover
                isPopoverOpen={isRevenueColumnHeaderIsOpen}
                position="right"
                isHtmlElement
                content={`The total gross sales for the ${currentTab.title}`}
                setIsPopoverOpen={setIsRevenueColumnHeaderIsOpen}
                iconClassNames="sc-information cursor-pointer ml-1"
              />
            </div>
            <button
              type="button"
              className={getSortClass(getRevenueKey())}
              onClick={() => handleSorting(getRevenueKey())}
            >
              {/* button content */}
            </button>
          </div>
        </th>
      </tr>
      <tr>
        {providerSpecificCells(
          tableFlattenedData.providerTitleRow,
          'providerQuantitiesTitle',
        )}
        {providerSpecificCells(
          tableFlattenedData.providerTitleRow,
          'providerRevenuesTitle',
        )}
      </tr>
    </thead>
  );

  // TABLE BODY
  const renderTableRows = tableRowData => {
    if (!tableRowData) {
      return null;
    }

    const providerDataRow = tableRowData.providerDataRow.map(row => (
      <tr key={row.title}>
        <td className={`${tableCellBaseClasses}`}>{row.title}</td>
        {providerSpecificCells(row, 'providerQuantities')}
        {providerSpecificCells(row, getProviderRevenueKey())}
      </tr>
    ));
    return providerDataRow;
  };

  return (
    <div className="bg-white w-full pb-4 rounded-b-lg">
      <div className="overflow-x-auto mb-8">
        <table className="table-auto w-full" id="item-breakdown-provider-table">
          {renderTableHeader}
          <tbody>
            {!isEmpty(tableFlattenedData.providerDataRow)
              ? renderTableRows(tableFlattenedData)
              : null}
            {isEmpty(tableFlattenedData?.providerDataRow)
              ? renderNoDataRow
              : null}
          </tbody>
        </table>
      </div>
      {!isEmpty(tableFlattenedData?.providerDataRow) ? pagination : null}
    </div>
  );
};

ItemBreakDownTableWithProvider.propTypes = {
  tableData: PropTypes.any,
  currentTab: PropTypes.any,
  analyticsProviders: PropTypes.array,
  itemBreakDownDataLoading: PropTypes.bool,
};

export default ItemBreakDownTableWithProvider;
